/* eslint-disable consistent-return */
import { showWebview, downgrade } from '@mx/neo';
import { getStorageItem, normalizeError, parseJSON } from '@mx/pay-common';
import { toast } from '@mx/vix';
import {
  PayType,
  env,
  monitor,
  PageType,
  PAY_RESULT_FOR_DEFER,
  PAY_TOKEN,
  removeStorage,
  setCommonData,
} from '@/common';
import type { GetPaydelayDisplayConsultPayLoad } from '@/store/constants';
import { initPage } from './init';
import { deferIndependenceSignFail } from './callbackNativeRouter';
import { storeInstance } from '@/store';

/**
 * 封装支付流程签约首页业务参数
 * @param data
 */
function wrapIndexOuterBusinessData(data: NeoCashierParams) {
  const initialData: Record<string, any> = parseJSON(data.half_page_render_data?.initialData, () => {}) || {};
  const extra_data = data.extra_data || initialData.extra_data;
  // 设置接入来源（web-cashier 接入先用后付，新增）
  env.setAccessSource(initialData.access_source);

  if (extra_data) {
    // 支付流程签约，直接将业务方的传过来的参数 extra_data 传给后端
    env.setOuterBusinessData(extra_data);

    // 解析业务方传递的 extra_data，此处包含 scorePlanId,creditType 等参数，方便其他地方使用
    // eslint-disable-next-line no-useless-catch
    try {
      const extraDataObj = JSON.parse(extra_data);
      env.setCoreBusinessParams(extraDataObj);
      env.setPayType(extraDataObj.payType || PayType.PayDelay);
    } catch (error) {
      // 解析失败，抛出异常，外层会直接降级到标准收银台
      throw error;
    }
  }
}

/**
 * 降级至标准收银台
 * @param error
 */
export function downgradeCashierByNEO() {
  downgrade({
    degrade_url: '',
    cashier_type: 'hybrid_standard_cashier',
  })
    .then(() => {
      monitor.captureInfo('[降级]downgrade 成功');
    })
    .catch((error) => {
      monitor.captureJSErrorMessage('[降级]downgrade 失败', { error: normalizeError(error) });
    });
}

/**
 * 封装独立签约首页业务数据
 */
function wrapHomeOuterBusinessData() {
  // 1. 独立签约需要自行拼接 homePage 里的 outer_Business_data 参数
  const outerBusinessData = {
    scorePlanId: env.scorePlanIdFromUrl,
    creditType: env.creditType,
    iphPayMerchantNo: env.iphPayMerchantNo,
    wxAppId: env.wxAppId,
  };
  env.setOuterBusinessData(JSON.stringify(outerBusinessData));
  // 独立签约需要处理 extra_data
  env.setCoreBusinessParams(outerBusinessData);
}

/**
 * 二次处理 NEO 参数隧道桥返回值
 * 打开半屏页面
 * @param type 页面类型
 * @returns Promise
 */
export function initPagePromise(type: PageType, url?: string): Promise<any> {
  return initPage(url)
    .then((data) => {
      monitor.captureResourceInfoMessage(`[info]进入先用后付${type}页面`);

      // 1. 设置接口请求公共数据
      setCommonData({
        ...env.commonParams,
      });

      // 2. 接口请求数据封装
      switch (type) {
        case PageType.index:
          // iOS容器泄漏加监控
          if (getStorageItem('PAY_DEFER_SUCCESS_TRADENO') === env.inpayBasicBusinessParams.tradeno) {
            monitor.captureJSErrorMessage('[bridge]iOS容器泄漏,重入paydefer页面', {
              tradeno: env.inpayBasicBusinessParams.tradeno,
            });
          }
          wrapIndexOuterBusinessData(data);
          break;
        case PageType.home:
          wrapHomeOuterBusinessData();
          break;
        case PageType.paydelay: {
          if (getStorageItem('PAY_DELAY_SUCCESS_TRADENO') === env.inpayBasicBusinessParams.tradeno) {
            monitor.captureMessage({
              name: '先用后付前置收银支付成功之后又打开页面',
              msg: `因为iOS容器泄漏的原因导致容器没有销毁，可能会再次进入页面,tradeno=${env.inpayBasicBusinessParams.tradeno}`,
            });
            return;
          }
          showWebview();
          toast.show('loading', '', {
            delay: 0,
          });
          wrapIndexOuterBusinessData(data);
          const store = storeInstance();
          const initViewPromise = store.dispatch<GetPaydelayDisplayConsultPayLoad>(
            {
              type: 'paydelaycashier/getPaydelayDisplayConsultDataHandle',
              requestChannel: 'prefetch',
            },
            { root: true }
          );
          return initViewPromise.then((res) => {
            monitor.captureMessage({
              name: '获取getPaydelayDisplayConsultDataHandle成功',
              msg: normalizeError(res),
            });
          });
        }
        case PageType.payment:
        default:
          break;
      }
      env.setParamTunnelTime(Date.now());
      showWebview();
      // 4. 进入页面清理本地存储等信息，避免出异常
      removeStorage(PAY_TOKEN);
      removeStorage(PAY_RESULT_FOR_DEFER);
    })
    .catch((error) => {
      monitor.captureJSErrorMessage(`[bridge]${type}页面参数隧道桥调用失败`, { error: normalizeError(error) });
      switch (type) {
        case PageType.index:
          downgradeCashierByNEO();
          break;
        case PageType.paydelay:
          throw error;
        case PageType.home:
          deferIndependenceSignFail();
          break;
        case PageType.payment:
        default:
          break;
      }
    });
}
